import React, { useState } from 'react';
import { Box, Button, Callout, Collapse, Flex, FlexColumn, Text } from 'core/components';
import { TOOL_CALL_STATUS } from '@kentik/ui-shared/kaizen/constants';
import { getToolDisplayName, createToolResultComponent, hasToolResultView } from '../ToolResultFactory';
import { ReasoningDetails, ReasoningContent } from '../KaizenChatSession.styles';
import Markdown from '../../journeys/Markdown';
import KaizenToolResult from '../KaizenToolResult';
import ToolResultCard from './ToolResultCard';
import ThinkingIndicator from './ThinkingIndicator';

const noop = () => {};

function getAgentDisplayName(agentName) {
  if (!agentName) {
    return 'Sub-Agent';
  }
  return getToolDisplayName(agentName);
}

// Renders a single sub-agent tool call as a compact card with its result "dropped" inline
// directly beneath it — mirroring the top-level conversation transcript, so the overlay reads
// top-to-bottom (reasoning -> call -> result -> next step). Nested sub-agent / MCP calls recurse
// through createToolResultComponent, which renders their markdown answer inline as well.
const ToolCallWithResult = ({ toolCall, onSelectToolResult, sessionModel }) => {
  const showInlineResult = toolCall.status === TOOL_CALL_STATUS.SUCCESS && hasToolResultView(toolCall.tool_name);

  return (
    <FlexColumn gap="4px">
      <KaizenToolResult tool={toolCall} onSelectToolResult={onSelectToolResult} sessionModel={sessionModel} compact />
      {showInlineResult && (
        <Box pl="16px">{createToolResultComponent(toolCall, { isInlineMode: true, onCloseToolResult: noop })}</Box>
      )}
    </FlexColumn>
  );
};

const TranscriptStep = ({ step, stepIndex, isReasoningOpen, onToggleReasoning, onSelectToolResult, sessionModel }) => {
  const stepId = step.id || stepIndex;
  const { llm_response = {}, toolCalls } = step;
  const isProcessing = !step.processing_status || step.processing_status === 'PROCESSING';

  return (
    <FlexColumn gap="4px">
      {llm_response?.reasoning && (
        <>
          <Button
            small
            minimal
            color="muted"
            icon={isReasoningOpen ? 'caret-up' : 'caret-down'}
            style={{ alignSelf: 'flex-start' }}
            text={isReasoningOpen ? 'Hide reasoning' : 'Show reasoning'}
            onClick={() => onToggleReasoning(stepId)}
          />
          <Collapse isOpen={isReasoningOpen}>
            <ReasoningDetails>
              <ReasoningContent>
                <Markdown>{llm_response.reasoning}</Markdown>
              </ReasoningContent>
            </ReasoningDetails>
          </Collapse>
        </>
      )}

      {llm_response?.final_answer && step.processing_status === 'COMPLETED' && (
        <Markdown>{llm_response.final_answer}</Markdown>
      )}

      {toolCalls?.length > 0 && (
        <FlexColumn gap="6px">
          {toolCalls.map((toolCall) => (
            <ToolCallWithResult
              key={toolCall.id || toolCall.tool_tracking_id}
              toolCall={toolCall}
              onSelectToolResult={onSelectToolResult}
              sessionModel={sessionModel}
            />
          ))}
        </FlexColumn>
      )}

      {step.error_message && step.error_message !== 'SCHEMA_FAILED' && (
        <Callout intent="danger">
          <Text small>Error: something went wrong</Text>
        </Callout>
      )}

      {isProcessing && !toolCalls?.length && !llm_response?.reasoning && <ThinkingIndicator />}
    </FlexColumn>
  );
};

const SubAgentTranscriptPanel = ({ item, sessionModel, onSelectToolResult, onCloseToolResult }) => {
  const { agentName, steps, reasoning, toolCalls = [] } = item;
  const [reasoningOpenState, setReasoningOpenState] = useState({});

  const handleToggleReasoning = (stepId) => {
    setReasoningOpenState((prev) => ({ ...prev, [stepId]: !(prev[stepId] ?? true) }));
  };

  const hasSteps = steps && steps.length > 0;
  const isStillProcessing = hasSteps && steps.some((s) => !s.processing_status || s.processing_status === 'PROCESSING');

  return (
    <ToolResultCard
      tool={item}
      onCloseToolResult={onCloseToolResult}
      className="tool-result-sub-agent-transcript"
      title={`${getAgentDisplayName(agentName)} details`}
    >
      <Flex flexDirection="column" p={2} pt={0} gap={2} flex={1} overflow="auto">
        {hasSteps ? (
          <>
            {steps.map((step, index) => (
              <TranscriptStep
                key={step.id || index}
                step={step}
                stepIndex={index}
                isReasoningOpen={reasoningOpenState[step.id || index] ?? true}
                onToggleReasoning={handleToggleReasoning}
                onSelectToolResult={onSelectToolResult}
                sessionModel={sessionModel}
              />
            ))}
            {isStillProcessing && <ThinkingIndicator />}
          </>
        ) : (
          <>
            {reasoning && <Markdown>{reasoning}</Markdown>}
            {toolCalls.length > 0 && (
              <FlexColumn gap={1}>
                <Text small fontWeight="bold" muted>
                  Tool calls
                </Text>
                {toolCalls.map((toolCall) => (
                  <ToolCallWithResult
                    key={toolCall.id || toolCall.tool_tracking_id}
                    toolCall={toolCall}
                    onSelectToolResult={onSelectToolResult}
                    sessionModel={sessionModel}
                  />
                ))}
              </FlexColumn>
            )}
            {!reasoning && toolCalls.length === 0 && <Text muted>No sub-agent activity to display.</Text>}
          </>
        )}
      </Flex>
    </ToolResultCard>
  );
};

export default SubAgentTranscriptPanel;
